import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../../services/api";
import { useToast } from "../../../contexts/ToastContext";
import {
  NotificationSettings,
  UpdateNotificationSettingsPayload,
} from "../../../types/grants";

/**
 * Panel for managing global notification settings
 */
const NotificationSettingsPanel: React.FC = () => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  // Fetch settings
  const { data: settings, isLoading } = useQuery<NotificationSettings>({
    queryKey: ["grants-notification-settings"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/alerts/settings");
      return data;
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateNotificationSettingsPayload) => {
      const { data } = await apiClient.put("/grants/alerts/settings", payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-notification-settings"] });
      addToast({ message: "Ustawienia zapisane", type: "success" });
    },
    onError: () => {
      addToast({ message: "Nie udało się zapisać ustawień", type: "error" });
    },
  });

  const handleGlobalToggle = () => {
    if (settings) {
      updateMutation.mutate({ globalEnabled: !settings.globalEnabled });
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-neutral-200 rounded w-1/4"></div>
          <div className="h-8 bg-neutral-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!settings) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
      <h2 className="text-lg font-medium text-neutral-900 mb-4">
        Ustawienia powiadomień
      </h2>

      <div className="space-y-6">
        {/* Global toggle */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-neutral-700 text-left">
              Powiadomienia email
            </h3>
            <p className="text-sm text-neutral-500 text-left">
              Włącz lub wyłącz wszystkie powiadomienia email
            </p>
          </div>
          <button
            onClick={handleGlobalToggle}
            disabled={updateMutation.isPending}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              settings.globalEnabled ? "bg-primary-600" : "bg-neutral-300"
            } ${updateMutation.isPending ? "opacity-50" : ""}`}
            aria-label={
              settings.globalEnabled
                ? "Wyłącz powiadomienia"
                : "Włącz powiadomienia"
            }
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.globalEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Status info */}
        {(settings.sendingPaused || !settings.isEmailValid) && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex gap-3">
              <svg
                className="h-5 w-5 text-amber-600 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-amber-800">
                  Wysyłanie wstrzymane
                </h4>
                <p className="text-sm text-amber-700 mt-1">
                  {settings.sendingPausedReason ||
                    (!settings.isEmailValid
                      ? "Adres email jest nieprawidłowy lub niedostarczalny."
                      : "Wysyłanie powiadomień zostało wstrzymane.")}
                </p>
                {settings.bounceCount > 0 && (
                  <p className="text-sm text-amber-600 mt-1">
                    Liczba odrzuceń: {settings.bounceCount}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSettingsPanel;
